import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiPost } from "../api";
import { useAuth } from "../auth/AuthContext";
import { PlanBadge, RoleBadge } from "../components/PlanBadge";
import { Skeleton, SkeletonStatGrid, Spinner } from "../components/Loaders";
import { Icon } from "../components/Icons";
import { PageIntro, Surface } from "../components/Visual";

export function Account() {
  const { firebaseUser, profile, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"portal" | "upgrade" | "plus" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const upgradedPlan = params.get("upgraded");
  const justUpgraded = upgradedPlan === "1" || upgradedPlan === "pro" || upgradedPlan === "plus";

  useEffect(() => {
    if (justUpgraded) {
      // Stripe webhook may lag - refresh a couple times.
      const ids = [500, 2000, 5000].map((ms) =>
        setTimeout(() => refreshProfile(), ms),
      );
      return () => ids.forEach(clearTimeout);
    }
  }, [justUpgraded, refreshProfile]);

  const onUpgrade = async (plan: "pro" | "plus" = "pro") => {
    setError(null);
    setBusy(plan === "plus" ? "plus" : "upgrade");
    try {
      const { url } = await apiPost<{ url: string }>("/api/billing/checkout", { plan });
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onManageBilling = async () => {
    setError(null);
    setBusy("portal");
    try {
      const { url } = await apiPost<{ url: string }>("/api/billing/portal");
      window.location.href = url;
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!profile) {
    return (
      <main className="page container">
        <Skeleton variant="title" width={220} style={{ marginBottom: 24 }} />
        <div style={{ marginBottom: 20 }}>
          <SkeletonStatGrid count={3} />
        </div>
        <div className="rationale-block">
          <Skeleton variant="title" width={180} style={{ marginBottom: 12 }} />
          <Skeleton variant="text" width="100%" style={{ marginBottom: 6 }} />
          <Skeleton variant="text" width="80%" />
        </div>
      </main>
    );
  }

  const isPlus = profile.plan === "plus";
  const isPro = profile.plan === "pro" || isPlus;
  const used = profile.buildsToday ?? 0;
  const cap = profile.dailyCap;
  const remaining = profile.buildsRemaining;

  return (
    <main className="page container account-page">
      <PageIntro
        eyebrow="Account"
        icon="user"
        title={<>Your <span className="grad">brewer profile.</span></>}
        body="Manage plan status, daily build quota, billing, and quick routes back into the app."
      />

      {justUpgraded && (
        <div className="quota-banner">
          <span>
            Welcome to {upgradedPlan === "plus" ? "Unlimited Plus" : "Unlimited"} -
            your plan will update within a few seconds.
          </span>
        </div>
      )}
      {error && <div className="form-error">{error}</div>}

      <div className="account-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <h4>Email</h4>
          <div className="value small">
            {profile.email ?? firebaseUser?.email}{" "}
            <RoleBadge role={profile.role} />
          </div>
        </div>
        <div className="stat-card">
          <h4>Plan</h4>
          <div className="value small">
            <PlanBadge plan={profile.plan} />{" "}
            {isPlus ? "Unlimited Plus" : isPro ? "Unlimited builds" : `${cap ?? 3} builds/day`}
          </div>
        </div>
        <div className="stat-card">
          <h4>Builds used today</h4>
          <div className="value">
            {used}
            {cap != null && <span className="muted"> / {cap}</span>}
          </div>
          {!isPro && remaining != null && (
            <div className="hint" style={{ marginTop: 4 }}>
              {remaining} remaining. Resets at midnight ET.
            </div>
          )}
        </div>
      </div>

      <Surface className="account-subscription">
        <h3>Subscription</h3>
        {isPlus ? (
          <p>
            You're on <strong>Unlimited Plus</strong>. Unlimited builds, unlimited
            Bloomy chat, priority queue, cancel anytime.
          </p>
        ) : isPro ? (
          <p>
            You're on <strong>Foil Bloom Unlimited</strong>. Unlimited builds,
            priority queue, and 10 Bloomy messages every 5 hours.
          </p>
        ) : (
          <p>
            You're on the <strong>Free</strong> plan (3 builds/day). Upgrade to
            Unlimited for unlimited deck generation and Bloomy access.
          </p>
        )}
        <div className="actions">
          {isPro ? (
            <>
              {!isPlus && (
                <button
                  type="button"
                  className="primary"
                  onClick={() => onUpgrade("plus")}
                  disabled={busy !== null}
                >
                  {busy === "plus" ? (
                    <>
                      <Spinner className="button-spinner" />
                      Opening checkout...
                    </>
                  ) : (
                    "Upgrade to Plus — $20/mo"
                  )}
                </button>
              )}
              <button
                className="secondary"
                onClick={onManageBilling}
                disabled={busy !== null}
              >
                {busy === "portal" ? (
                  <>
                    <Spinner className="button-spinner" />
                    Opening portal...
                  </>
                ) : (
                  "Manage billing"
                )}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="primary"
              onClick={() => onUpgrade("pro")}
              disabled={busy !== null}
            >
              {busy === "upgrade" ? (
                <>
                  <Spinner className="button-spinner" />
                  Opening checkout...
                </>
              ) : (
                "Upgrade to Unlimited — $3/mo"
              )}
            </button>
          )}
          {profile.stripeCustomerId && !isPro && (
            <button
              className="secondary"
              onClick={onManageBilling}
              disabled={busy !== null}
            >
              Manage past billing
            </button>
          )}
        </div>
      </Surface>

      <Surface className="account-subscription">
        <h3>Quick links</h3>
        <ul>
          <li><Link to="/build">Open the deck builder</Link></li>
          <li><Link to="/bloomy">Chat with Bloomy</Link></li>
          <li><Link to="/analyze">Analyze a decklist</Link></li>
          <li><Link to="/pricing">Compare plans</Link></li>
          {profile.isAdmin && <li><Link to="/admin">Admin console</Link></li>}
        </ul>
      </Surface>

      <div className="actions">
        <button className="danger" onClick={onLogout}><Icon name="login" size={16} />Sign out</button>
      </div>
    </main>
  );
}
